import React from 'react';
import styles from '../../styles/query/FilterChecklist.module.scss';

/**
 * Filter Checklist Component
 *
 * Radio buttons for "=" and "!=" (single value); checkboxes for "in" and "notIn" (multi-select).
 * 
 * @param {Object} props
 * @param {Array} props.options - Available options
 * @param {Array} props.selectedValues - Currently selected values
 * @param {function} props.onSelectionChange - Handler for selection change
 * @param {Object} props.field - Field metadata
 * @param {string} props.operator - Current operator ('=', '!=', 'in', 'notIn')
 */
export function FilterChecklist({ options = [], selectedValues = [], onSelectionChange, field, operator = '=' }) {
  const useRadioButtons = operator === '=' || operator === '!=';
  const radioGroupName = `filter-${field?.name || 'field'}`;

  const allowMultiSelect = React.useMemo(() => {
    return operator === 'in' || operator === 'notIn';
  }, [operator]);

  const handleRadioSelect = (value) => {
    onSelectionChange([value]);
  };

  const handleToggle = (value) => {
    const valueKey = typeof value === 'string' ? value : (value.value || value.id || String(value));
    const isSelected = selectedValues.some(selected => {
      const selectedKey = typeof selected === 'string' ? selected : (selected.value || selected.id || String(selected));
      return selectedKey === valueKey;
    });

    let newSelection;
    
    if (isSelected) {
      // Deselect: remove from selection
      newSelection = selectedValues.filter(selected => {
        const selectedKey = typeof selected === 'string' ? selected : (selected.value || selected.id || String(selected));
        return selectedKey !== valueKey;
      });
    } else {
      // Select: add to selection
      if (allowMultiSelect) {
        // Multi-select: add to existing selection
        newSelection = [...selectedValues, value];
      } else {
        // Single-select: replace entire selection with just this value
        newSelection = [value];
      }
    }

    onSelectionChange(newSelection);
  };

  const getOptionLabel = (option) => {
    if (typeof option === 'string') return option;
    return option.label || option.name || String(option);
  };

  const getOptionValue = (option) => {
    if (typeof option === 'string') return option;
    return option.value || option.id || option;
  };

  const isOptionSelected = (option) => {
    const optionValue = getOptionValue(option);
    return selectedValues.some(selected => {
      const selectedValue = typeof selected === 'string' ? selected : (selected.value || selected.id || String(selected));
      return selectedValue === optionValue;
    });
  };

  const isOptionDisabled = (option) => {
    if (typeof option === 'object' && option.disabled !== undefined) {
      return option.disabled;
    }
    return false;
  };

  if (options.length === 0) {
    return (
      <div className={styles.emptyState}>
        <p className={styles.emptyText}>No options available</p>
      </div>
    );
  }

  return (
    <div className={styles.checklistContainer}>
      {options.map((option, index) => {
        const isSelected = isOptionSelected(option);
        const isDisabled = isOptionDisabled(option);
        const label = getOptionLabel(option);
        const value = getOptionValue(option);

        return (
          <label
            key={`${value}-${index}`}
            className={`${styles.checklistItem} ${isSelected ? styles.selected : ''} ${isDisabled ? styles.disabled : ''}`}
          >
            <input
              type={useRadioButtons ? 'radio' : 'checkbox'}
              name={useRadioButtons ? radioGroupName : undefined}
              checked={isSelected}
              disabled={isDisabled}
              onChange={() =>
                useRadioButtons ? handleRadioSelect(option) : handleToggle(option)
              }
              className={useRadioButtons ? undefined : styles.checkbox}
            />
            <span className={styles.checklistLabel}>{label}</span>
          </label>
        );
      })}
    </div>
  );
}

export default FilterChecklist;

